// The ready sweep's carrier-name rule, ported (Task 13 fix round 1, I1): a record whose carrier Account name the sweep refuses is never called
// (lib/claims_controller/ready_sweep.js readyCandidate PLACEHOLDER_CARRIER / PUBLIC_ENTITY_CARRIER, its placeholderCarrier and publicEntityCarrier
// in lib/claims_controller/staff_rows.js), so the files lane never offers "Approve and send the AI" for it. The same rule as the controller's, word
// for word: core/test/sweep-carrier-parity.test.ts runs both on a generated corpus and checks every pattern below against the controller's source.
// Pure, names never logged.
// lib/claims_controller/finders.js GENERIC, PLACEHOLDER, carrierTokens, rowCarrierText
const GENERIC = new Set(['insurance', 'insurances', 'ins', 'company', 'companies', 'co', 'corp', 'corporation', 'inc', 'incorporated', 'llc', 'ltd',
    'the', 'of', 'and', 'claims', 'claim', 'department', 'dept', 'team', 'unit', 'level', 'services', 'service', 'center', 'centre', 'group',
    'office', 'division']);
const PLACEHOLDER = /^(unknown|unk|pending|n\/?a|none|tbd|tba|delete|def ins|acord|self insured|self-insured|other)$/i;
function carrierTokens(name) {
    const words = String(name == null ? '' : name).toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9 ]+/g, ' ').split(/\s+/).filter(Boolean);
    return [...new Set(words.filter((w) => !GENERIC.has(w)))];
}
const isCarrierText = (s) => s != null && !PLACEHOLDER.test(String(s).trim()) && carrierTokens(s).length > 0;
function rowCarrierText(rowName) {
    let s = String(rowName == null ? '' : rowName).trim();
    for (let i = 0; i < 3; i += 1)
        s = s.replace(/^(DEF|PLT|1P|3P|UM\/UIM|UIM|UM|PIP|MEDPAY|AT\s*FAULT|OTHER\s*PARTY)\s*[-:]\s*/i, '').trim();
    return isCarrierText(s) ? s.slice(0, 80) : null;
}
// lib/claims_controller/carrier_directory.json kindPatterns (in order: the first that matches names the kind)
export const KIND_PATTERNS = Object.freeze([
    { kind: 'government', pattern: '\\b(city|county|state|town|village) of\\b|\\b(metro|transit|caltrans|department of transportation|school district|unified school)\\b' },
    { kind: 'self_insured', pattern: '\\bself[ -]?insured\\b' },
    { kind: 'broker', pattern: '\\b(agency|agencies|brokerage|insurance agent|insurance agents)\\b' },
    { kind: 'placeholder', pattern: '^(no|none|def ins|def|plt|acord|(unknown|unk)( (unknown|unk))+|unknown|unk|pending|tbd|tba|n/?a|disregard|duplicate|dupe|delete|uninsured|uninsured motorist|hit (and )?run|unknown driver hit run|unknown defendant|no coverage|prop 213|1st party prop 213|no await tcr|policy|auto policy|commercial policy|rental|other)$' },
]);
const KINDS = KIND_PATTERNS.map((k) => ({ kind: k.kind, re: new RegExp(k.pattern, 'i') }));
// lib/claims_controller/carrier_contact.js carrierText, kindOfName
function carrierText(name) {
    const s = String(name || '').replace(/^\s*[-.]+\s*/, '').trim();
    return rowCarrierText(s) || null;
}
function kindOfName(name) {
    const text = carrierText(name);
    const plain = String(text || name || '').replace(/^(DEF|PLT|1P|3P)\s*[-:]\s*/i, '').replace(/\b(insurance|ins|company|co|inc|llc|corp|group|services|svcs)\b\.?/ig, '').replace(/[^a-z0-9/ ]/ig, ' ').replace(/\s+/g, ' ').trim();
    if (!text || !plain)
        return 'placeholder';
    for (const k of KINDS)
        if (k.re.test(plain))
            return k.kind;
    if (/^a0k[a-z0-9]{12,15}$/i.test(plain))
        return 'placeholder';
    return null;
}
// lib/claims_controller/twin.js CONSOLE_PLACEHOLDER, isPlaceholderCarrier; lib/claims_controller/companion_copy.js PLACEHOLDER_CARRIER
const CONSOLE_PLACEHOLDER = /^(unknown|unk|pending|n\/?a|tbd|tba|delete|none|insurance|def ins(urance)?|plt ins(urance)?)$/i;
const PLACEHOLDER_CARRIER = /^(unknown|unk|pending|n\/?a|none|tbd|tba|delete|def ins|def ins -|acord|self insured|self-insured|other|uninsured|no insurance)$/i;
function twinPlaceholder(name) {
    const n = String(name == null ? '' : name).trim().replace(/[\s-]+$/, '').trim();
    if (!n)
        return true;
    return CONSOLE_PLACEHOLDER.test(n) || PLACEHOLDER_CARRIER.test(n) || kindOfName(n) === 'placeholder';
}
// lib/claims_controller/staff_rows.js NO_CARRIER_NAME, gatePlaceholder, placeholderCarrier, PUBLIC_ENTITY, publicEntityCarrier
const NO_CARRIER_NAME = /^(no insurance|uninsured|unknown\b.*|.*\bparked vehicle|pending\b.*|none\b.*)$/i;
const GATE_WORDS = new Set(['UNKNOWN', 'UNK', 'DUPLICATE', 'DUP']);
const UNKNOWNISH = /^(UNK|UKN)[A-Z]*$/;
const GATE_NAMES = new Set(['N A', 'NA', 'TBD', 'TBA', 'ACORD', 'ACORD INSURANCE', 'SELF INSURED']);
function gatePlaceholder(name) {
    const t = String(name == null ? '' : name).toUpperCase().replace(/[^A-Z0-9]+/g, ' ').trim().split(' ').filter(Boolean);
    if (!t.length)
        return false;
    const s = t.join(' ');
    if (t.every((x) => GATE_WORDS.has(x) || UNKNOWNISH.test(x)) && t.some((x) => UNKNOWNISH.test(x)))
        return true;
    return t[0] === 'PENDING' || t[0] === 'DELETE' || GATE_NAMES.has(s) || s.startsWith('DEF INS');
}
const PUBLIC_ENTITY = /^(city|county|state|town|village)\s+of\b|\b(department of|transit authority|unified school district|metropolitan transportation)\b/i;
/** The sweep's placeholder rule (staff_rows.js placeholderCarrier): no real insurer is named */
export const sweepPlaceholderCarrier = (name) => twinPlaceholder(name) || NO_CARRIER_NAME.test(String(name == null ? '' : name).trim()) || gatePlaceholder(name);
/** The sweep's public-agency rule (staff_rows.js publicEntityCarrier): a government claim form, never a call */
export const sweepPublicEntityCarrier = (name) => PUBLIC_ENTITY.test(String(name == null ? '' : name).trim());
// lib/claims_controller/ready_sweep.js STATE_FARM (final review 10-29, deferred minor 8: the console's lanes read "State Farm" only with a space, so
// "StateFarm" got a plain Approve while the controller holds it for the online lane)
const STATE_FARM = /\bstate\s*farm\b/i;
/** The controller's State Farm name rule (ready_sweep.js STATE_FARM): State Farm is never dialled, a new number can restart its online filing */
export const sweepStateFarm = (name) => STATE_FARM.test(String(name == null ? '' : name));
/** A carrier name the ready sweep never calls (ready_sweep.js readyCandidate PLACEHOLDER_CARRIER or PUBLIC_ENTITY_CARRIER) */
export const sweepRefusesCarrier = (name) => sweepPlaceholderCarrier(name) || sweepPublicEntityCarrier(name);
//# sourceMappingURL=sweep_carrier.js.map